import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  Search, RefreshCw, Package, XCircle, Loader2,
  Send, Printer, Scale, MapPin
} from 'lucide-react'
import { shipping } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { CarrierLogo, CountryFlag, CarrierConnectionDot, PaymentBadge } from '@/components/CarrierLogos'
import QueueOrderDetail from './QueueOrderDetail'
import LabelPrintModal from './LabelPrintModal'
import { useShippingContext } from '@/contexts/ShippingContext'

// Opciones de transportista
const CARRIERS = [
  { code: 'mrw', name: 'MRW', service: 'Entrega en 1 día laborable' },
  { code: 'gls', name: 'GLS Spain', service: 'Entrega en 24-48h' },
  { code: 'ctt', name: 'CTT Express', service: 'Entrega en 1 día laborable' },
  { code: 'dhl', name: 'DHL Parcel', service: 'Europa 3-5 días laborables' },
]

import type { QueueItem } from '@/lib/api'

export default function ShippingQueueTab() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { navigateToTracking } = useShippingContext()

  // Estado local
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedQueueItem, setSelectedQueueItem] = useState<QueueItem | null>(null)
  const [printModal, setPrintModal] = useState<{ pdfBlob: Blob; trackingNumber?: string } | null>(null)

  // Edición inline
  const [editingItem, setEditingItem] = useState<number | null>(null)
  const [editValues, setEditValues] = useState<{ carrier: string; packages: number; weight: number }>({
    carrier: 'mrw',
    packages: 1,
    weight: 0.5
  })

  // Query para pedidos en la cola de envío
  const queueQuery = useQuery({
    queryKey: ['shipping-queue'],
    queryFn: async () => {
      const response = await shipping.listQueue()
      return response.items
    },
  })

  // Query para estado de conexiones
  const connectionQuery = useQuery({
    queryKey: ['shipping-connections'],
    queryFn: () => shipping.testConnections(),
    staleTime: 60000,
  })

  // Mutation para actualizar item de cola
  const updateQueueItemMutation = useMutation({
    mutationFn: async ({ orderId, data }: { orderId: number; data: { carrier_code?: string; packages?: number; weight?: number } }) => {
      return shipping.updateQueueItem(orderId, data)
    },
    onSuccess: () => {
      toast.success('Datos actualizados')
      queryClient.invalidateQueries({ queryKey: ['shipping-queue'] })
    },
    onError: (error: any) => {
      toast.error('Error al actualizar', error.response?.data?.detail || error.message)
    },
  })

  // Mutation para crear envío (genera etiqueta - el backend elimina de cola automáticamente)
  const createShipmentMutation = useMutation({
    mutationFn: async ({ item, isTestMode }: { item: QueueItem; isTestMode: boolean }) => {
      return await shipping.createShipment({
        order_id: item.order_id,
        carrier_code: item.carrier_code,
        service_type: item.carrier_code === 'dhl' && item.service_point ? 'service_point' : undefined,
        packages: item.packages,
        weight: item.weight,
        notes: item.carrier_notes ?? undefined,
        with_return: item.with_return ?? false,  // Envío con retorno (persistido en la cola)
        test_mode: isTestMode
      })
    },
    onSuccess: async (data, { isTestMode }) => {
      const prefix = isTestMode ? '[TEST] ' : ''
      toast.success(`${prefix}Etiqueta generada: ${data.tracking_number}`)
      // Refrescar listas
      queryClient.invalidateQueries({ queryKey: ['shipping-queue'] })
      queryClient.invalidateQueries({ queryKey: ['shipping-queue-ids'] })
      queryClient.invalidateQueries({ queryKey: ['shipping-tracking'] })

      // Mostrar modal de impresión si hay ID de envío
      if (data.id) {
        try {
          const pdfBlob = await shipping.downloadLabel(data.id)
          setPrintModal({ pdfBlob, trackingNumber: data.tracking_number })
        } catch (error) {
          console.error('Error obteniendo etiqueta:', error)
        }
      }
    },
    onError: (error: any) => {
      toast.error('Error al generar etiqueta', error.response?.data?.detail || error.message)
    },
  })

  // Mutation para envío masivo (el backend elimina de cola automáticamente)
  const bulkShipMutation = useMutation({
    mutationFn: async ({ items, isTestMode }: { items: QueueItem[]; isTestMode: boolean }) => {
      const results = await Promise.allSettled(
        items.map(item => shipping.createShipment({
          order_id: item.order_id,
          carrier_code: item.carrier_code,
          service_type: item.carrier_code === 'dhl' && item.service_point ? 'service_point' : undefined,
          packages: item.packages,
          weight: item.weight,
          notes: item.carrier_notes ?? undefined,
          with_return: item.with_return ?? false,  // Envío con retorno (persistido en la cola)
          test_mode: isTestMode
        }))
      )

      // Recopilar IDs de envíos exitosos
      const successfulShipmentIds: number[] = []
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value?.id) {
          successfulShipmentIds.push(result.value.id)
        }
      })

      const success = results.filter(r => r.status === 'fulfilled').length
      const failed = results.filter(r => r.status === 'rejected').length

      return { success, failed, isTestMode, shipmentIds: successfulShipmentIds }
    },
    onSuccess: async (data) => {
      const prefix = data.isTestMode ? '[TEST] ' : ''
      toast.success(`${prefix}${data.success} envíos creados, ${data.failed} fallidos`)
      setSelectedItems([])
      queryClient.invalidateQueries({ queryKey: ['shipping-queue'] })
      queryClient.invalidateQueries({ queryKey: ['shipping-queue-ids'] })
      queryClient.invalidateQueries({ queryKey: ['shipping-tracking'] })

      // Mostrar modal con etiquetas combinadas si hay envíos exitosos
      if (data.shipmentIds.length > 0) {
        try {
          const pdfBlob = await shipping.getCombinedLabels(data.shipmentIds)
          setPrintModal({ pdfBlob, trackingNumber: `${data.shipmentIds.length} etiquetas` })
        } catch (error) {
          console.error('Error obteniendo etiquetas combinadas:', error)
          toast.error('Error al abrir etiquetas', 'No se pudieron cargar las etiquetas combinadas')
        }
      }
    },
    onError: (error: any) => {
      toast.error('Error en envío masivo', error.message)
    },
  })

  // Handlers
  const handleSelectAll = () => {
    if (!queueQuery.data) return
    if (selectedItems.length === queueQuery.data.length) {
      setSelectedItems([])
    } else {
      setSelectedItems(queueQuery.data.map(item => item.order_id))
    }
  }

  const handleSelectItem = (orderId: number) => {
    setSelectedItems(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    )
  }

  const handleEditStart = (item: QueueItem) => {
    setEditingItem(item.order_id)
    setEditValues({
      carrier: item.carrier_code,
      packages: item.packages,
      weight: item.weight
    })
  }

  const handleEditSave = (item: QueueItem) => {
    updateQueueItemMutation.mutate({
      orderId: item.order_id,
      data: {
        carrier_code: editValues.carrier,
        packages: editValues.packages,
        weight: editValues.weight
      }
    })
    setEditingItem(null)
  }

  const handleGenerateLabel = (item: QueueItem) => {
    createShipmentMutation.mutate({ item, isTestMode: false })
  }

  const handleBulkShip = () => {
    if (!queueQuery.data) return
    const items = queueQuery.data.filter(item => selectedItems.includes(item.order_id))
    bulkShipMutation.mutate({ items, isTestMode: false })
  }

  // Filtrar items
  const queueItems = queueQuery.data || []
  const filteredItems = searchQuery
    ? queueItems.filter(item =>
        item.order_reference.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.customer_name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : queueItems

  const connections = connectionQuery.data

  return (
    <div className="flex h-full">
      {/* Contenido principal */}
      <div className="flex-1 flex flex-col">
        {/* Toolbar */}
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por número de pedido..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-80 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => queueQuery.refetch()}
                disabled={queueQuery.isFetching}
                className="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <RefreshCw className={`w-5 h-5 ${queueQuery.isFetching ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-auto p-6">
          {/* Vista de detalle del pedido seleccionado */}
          {selectedQueueItem && (
            <div className="mb-6">
              <QueueOrderDetail
                item={selectedQueueItem}
                onClose={() => setSelectedQueueItem(null)}
                onLabelGenerated={() => {
                  setSelectedQueueItem(null)
                  queryClient.invalidateQueries({ queryKey: ['shipping-queue'] })
                }}
              />
            </div>
          )}

          {queueQuery.isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-teal-600 animate-spin mb-3" />
              <p className="text-slate-500 dark:text-slate-400">Cargando cola de envío...</p>
            </div>
          ) : queueQuery.isError ? (
            <div className="flex flex-col items-center justify-center py-16">
              <XCircle className="w-8 h-8 text-red-500 mb-3" />
              <p className="text-red-600 dark:text-red-400">Error cargando cola</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-8">
              <div className="text-center max-w-md mx-auto">
                <Package className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  No tienes pedidos en la Cola de Envíos
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-6">
                  O ya has imprimido todas las etiquetas de envío (si es así, ¡bien hecho!),
                  o echa un vistazo a tus pedidos en la pestaña de{' '}
                  <span className="text-teal-600 dark:text-teal-400 font-medium">Pedidos</span>{' '}
                  y ponte manos a la obra (¡tus clientes están esperando esos pedidos!).
                </p>

                {/* Ejemplo de cómo se ven los pedidos */}
                <div className="mt-8 text-left">
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                    Tus pedidos se verán así en la Cola de Envíos:
                  </p>
                  <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                    {/* Header ejemplo */}
                    <div className="grid grid-cols-[60px_1fr_80px_80px_80px_100px_100px] gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-900/50 text-xs text-slate-500 uppercase">
                      <div>Fuente</div>
                      <div>Fecha</div>
                      <div>ID</div>
                      <div>Destino</div>
                      <div>Total</div>
                      <div>Productos</div>
                      <div>Estado</div>
                    </div>
                    {/* Filas ejemplo */}
                    {[
                      { source: 'PS', country: 'ES', total: '€45.00', products: 2 },
                      { source: 'PS', country: 'PT', total: '€32.50', products: 1 },
                    ].map((example, idx) => (
                      <div key={idx} className="grid grid-cols-[60px_1fr_80px_80px_80px_100px_100px] gap-2 px-3 py-2 border-t border-slate-100 dark:border-slate-700/50 text-sm">
                        <div>
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                            <span className="text-white text-xs font-bold">{example.source}</span>
                          </div>
                        </div>
                        <div className="text-slate-400">--:-- --/--/----</div>
                        <div className="text-slate-400">------</div>
                        <div className="flex items-center gap-1">
                          <CountryFlag countryCode={example.country} />
                          <span className="text-slate-400">{example.country}</span>
                        </div>
                        <div className="text-slate-400">{example.total}</div>
                        <div className="text-slate-400 text-center">{example.products}</div>
                        <div>
                          <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-500 rounded">EJEMPLO</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-[40px_60px_1fr_100px_100px_120px_100px_100px_80px_140px] gap-2 px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 text-xs font-medium text-slate-500 uppercase">
                <div className="flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={selectedItems.length === filteredItems.length && filteredItems.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                </div>
                <div>Fuente</div>
                <div>Pedido</div>
                <div>Destino</div>
                <div>Transportista</div>
                <div>Bultos / Peso</div>
                <div>Total</div>
                <div>Pago</div>
                <div>Estado</div>
                <div>Acciones</div>
              </div>

              {/* Items */}
              <div className="divide-y divide-slate-100 dark:divide-slate-700/50">
                {filteredItems.map((item) => {
                  const isEditing = editingItem === item.order_id
                  const isSelected = selectedItems.includes(item.order_id)

                  return (
                    <div
                      key={item.order_id}
                      className={`grid grid-cols-[40px_60px_1fr_100px_100px_120px_100px_100px_80px_140px] gap-2 px-4 py-3 items-center hover:bg-slate-50 dark:hover:bg-slate-900/30 cursor-pointer ${
                        isSelected ? 'bg-teal-50 dark:bg-teal-900/20' : ''
                      } ${selectedQueueItem?.order_id === item.order_id ? 'ring-2 ring-teal-500 ring-inset bg-teal-50 dark:bg-teal-900/30' : ''}`}
                      onClick={() => setSelectedQueueItem(item)}
                    >
                      {/* Checkbox */}
                      <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectItem(item.order_id)}
                          className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                        />
                      </div>

                      {/* Fuente */}
                      <div className="flex items-center justify-center">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                          <span className="text-white font-bold text-xs">PS</span>
                        </div>
                      </div>

                      {/* Pedido */}
                      <div>
                        <div className="text-sm font-medium text-slate-900 dark:text-white">{item.order_reference}</div>
                        <div className="text-xs text-slate-500">{item.customer_name.slice(0, 25)}</div>
                      </div>

                      {/* Destino */}
                      <div className="flex items-center gap-1.5">
                        <CountryFlag countryCode={item.shipping_address.country_code} />
                        <span className="text-xs text-slate-600 dark:text-slate-400">
                          {item.shipping_address.country_code}
                        </span>
                        {item.service_point && (
                          <span
                            title={`📍 ${item.service_point.name}\n${item.service_point.address}, ${item.service_point.postal_code} ${item.service_point.city}`}
                            className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 cursor-help"
                          >
                            <MapPin className="w-2.5 h-2.5" />SP
                          </span>
                        )}
                      </div>

                      {/* Transportista */}
                      <div>
                        {isEditing ? (
                          <select
                            value={editValues.carrier}
                            onChange={(e) => setEditValues({ ...editValues, carrier: e.target.value })}
                            className="w-full px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                          >
                            {CARRIERS.map(c => (
                              <option key={c.code} value={c.code}>{c.name}</option>
                            ))}
                          </select>
                        ) : (
                          <CarrierLogo carrier={item.carrier_code} className="h-4 w-14" />
                        )}
                      </div>

                      {/* Bultos / Peso */}
                      <div className="flex items-center gap-2">
                        {isEditing ? (
                          <>
                            <input
                              type="number"
                              min="1"
                              value={editValues.packages}
                              onChange={(e) => setEditValues({ ...editValues, packages: parseInt(e.target.value) || 1 })}
                              className="w-12 px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                            />
                            <input
                              type="number"
                              min="0.1"
                              step="0.1"
                              value={editValues.weight}
                              onChange={(e) => setEditValues({ ...editValues, weight: parseFloat(e.target.value) || 0.5 })}
                              className="w-16 px-2 py-1 text-xs rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                            />
                          </>
                        ) : (
                          <span className="text-xs text-slate-600 dark:text-slate-400">
                            {item.packages} bulto{item.packages > 1 ? 's' : ''} / {item.weight} kg
                          </span>
                        )}
                      </div>

                      {/* Total */}
                      <div className="text-sm font-medium text-slate-900 dark:text-white">
                        €{item.total.toFixed(2)}
                      </div>

                      {/* Método de pago */}
                      <div className="flex items-center justify-center" title={item.payment_method}>
                        <PaymentBadge paymentMethod={item.payment_method} />
                      </div>

                      {/* Estado */}
                      <div>
                        <span className="px-2 py-1 text-xs font-medium rounded bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                          LISTO
                        </span>
                      </div>

                      {/* Acciones */}
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => handleEditSave(item)}
                              className="px-2 py-1 text-xs bg-teal-600 text-white rounded hover:bg-teal-700"
                            >
                              Guardar
                            </button>
                            <button
                              onClick={() => setEditingItem(null)}
                              className="px-2 py-1 text-xs text-slate-600 hover:text-slate-800"
                            >
                              Cancelar
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEditStart(item)}
                              className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded"
                              title="Editar"
                            >
                              <Scale className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleGenerateLabel(item)}
                              disabled={createShipmentMutation.isPending}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
                            >
                              <Printer className="w-3 h-3" />
                              Etiqueta
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Panel lateral - Envío Masivo y Estado Transportistas */}
      <div className="w-72 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 p-5 space-y-6">
        {/* Envío Masivo */}
        {selectedItems.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider">
              Envío Masivo
            </h3>
            <button
              onClick={handleBulkShip}
              disabled={bulkShipMutation.isPending}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50"
            >
              {bulkShipMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Enviar {selectedItems.length} pedido{selectedItems.length > 1 ? 's' : ''}
            </button>
          </div>
        )}

        {/* Estado de Transportistas */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white uppercase tracking-wider">
            Estado Transportistas
          </h3>

          <div className="space-y-3">
            {/* GLS */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50">
              <div className="flex items-center gap-3">
                <CarrierLogo carrier="gls" className="h-4 w-12" />
              </div>
              <CarrierConnectionDot connected={connections?.gls?.connected || false} />
            </div>

            {/* CTT */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50">
              <div className="flex items-center gap-3">
                <CarrierLogo carrier="ctt" className="h-4 w-12" />
              </div>
              <CarrierConnectionDot connected={connections?.ctt?.connected || false} />
            </div>

            {/* MRW */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-900/50">
              <div className="flex items-center gap-3">
                <CarrierLogo carrier="mrw" className="h-4 w-12" />
              </div>
              <CarrierConnectionDot connected={connections?.mrw?.connected || false} />
            </div>
          </div>
        </div>
      </div>

      {/* Modal de impresión de etiquetas */}
      {printModal && (
        <LabelPrintModal
          pdfBlob={printModal.pdfBlob}
          trackingNumber={printModal.trackingNumber}
          onClose={() => {
            setPrintModal(null)
            // Navegar a tracking después de cerrar
            if (navigateToTracking) {
              navigateToTracking()
            }
          }}
        />
      )}
    </div>
  )
}
